import { useCallback, useSyncExternalStore } from 'react';

export const EARLY_REACTION_REFETCH_DELAYS_MS: ReadonlyArray<number> = [0, 2000, 6000, 14000];

type ReactionRefresh = {
	startedAt: number;
	agentIdentityAccountId: string;
};

// Retain recent attempts for comment surfaces that mount after persistence. This is
// bounded page-lifetime state, not a durable reaction or session record.
const refreshes = new Map<string, ReactionRefresh>();
const listeners = new Map<string, Set<() => void>>();
const MAX_TRACKED_REFRESHES = 500;

const keyForComment = (cloudId: string, contentId: string, commentId: string): string =>
	JSON.stringify([cloudId, contentId, commentId]);

export function startCommentAgentReactionRefresh(
	cloudId: string,
	contentId: string,
	commentId: string,
	agentIdentityAccountId: string,
): void {
	if (refreshes.size >= MAX_TRACKED_REFRESHES) {
		const oldestKey = refreshes.keys().next().value;
		if (oldestKey) {
			refreshes.delete(oldestKey);
		}
	}
	const key = keyForComment(cloudId, contentId, commentId);
	refreshes.set(key, { startedAt: Date.now(), agentIdentityAccountId });
	listeners.get(key)?.forEach((listener) => listener());
}

export function useCommentAgentReactionRefresh(
	cloudId: string,
	contentId: string,
	commentId: string,
): ReactionRefresh | undefined {
	const key = keyForComment(cloudId, contentId, commentId);
	const subscribe = useCallback(
		(listener: () => void) => {
			const subscribers = listeners.get(key) ?? new Set<() => void>();
			listeners.set(key, subscribers);
			subscribers.add(listener);
			return () => {
				subscribers.delete(listener);
				if (!subscribers.size) {
					listeners.delete(key);
				}
			};
		},
		[key],
	);
	const getSnapshot = useCallback(() => refreshes.get(key), [key]);
	return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function clearCommentAgentReactionRefreshesForTesting(): void {
	refreshes.clear();
	listeners.clear();
}
